import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";

import React from "react";
import { Navigation, Pagination } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import { GameCard } from "@pages/casino/components";
import BettoContainerSlider from "@pages/home/betto/casinoGridSlider/containerSlider.jsx";

import { CasinoSliderContainer, TitleContainer } from "./elements.js";
import useController from "./useController.js";
import { getCardWidth } from "./utils.js";

const CasinoSlider = ({ isGamePage, data, type, sectionStartIndex, activeTab }) => {
	if (window.TENANT === "rollblock") {
		return (
			<RollblockCasinoSlider
				isGamePage={isGamePage}
				data={data}
				type={type}
				sectionStartIndex={sectionStartIndex}
			/>
		);
	}

	if (window.TENANT === "betto") {
		return (
			<BettoContainerSlider
				data={data}
				type={type || activeTab}
				activeTab={activeTab}
				isGamePage={isGamePage}
				containerPage={activeTab === "LiveCasino" ? "LiveCasino" : "Casino"}
			/>
		);
	}

	const {
		refs: { containerRef },
		helpers: { getSlidesPerGroup },
		state: { intersected, windowWidth, currentLanguage, userData },
	} = useController();

	const renderGameCard = (game, index, size = "1x1") => (
		<SwiperSlide
			key={index}
			style={{
				width: getCardWidth(size, windowWidth, window.TENANT),
			}}>
			<GameCard
				key={game?.id}
				size={size}
				game={game}
				type={type}
				isGamePage={isGamePage}
			/>
		</SwiperSlide>
	);

	const renderGridOptions = (grid, gridIndex) => {
		const items = [...(grid?.items || [])];
		const sectionIdx =
			sectionStartIndex !== undefined ? sectionStartIndex + gridIndex : undefined;

		return (
			<div
				key={grid?.id}
				data-testid={
					sectionIdx !== undefined ? `scrollview-section${sectionIdx}-games` : undefined
				}>
				<Swiper
					slidesPerView={"auto"}
					breakpoints={{ 1600: { slidesOffsetBefore: 50, slidesOffsetAfter: 50 } }}
					lazyPreloadPrevNext={1}
					slidesPerGroup={getSlidesPerGroup(windowWidth)}
					spaceBetween={10}
					modules={[Pagination, Navigation]}
					navigation={{ nextEl: ".swiper-button-next", prevEl: ".swiper-button-prev" }}>
					{items
						.sort((g1, g2) =>
							data?.type === "userLastPlayed" ? -1 : g1?.cardOrder - g2?.cardOrder
						)
						.map((item, index) => (
							<SwiperSlide
								key={index}
								style={{
									width: getCardWidth(
										item.options.cardSize,
										windowWidth,
										window.TENANT
									),
								}}>
								{item.options.cardType === "single" ? (
									<GameCard
										key={item.options.games[0]?.id}
										size={item.options.games[0]?.gameCardSize || "1x1"}
										game={data.gameList.find(
											(game) => game.id === item.options.games[0].id
										)}
										type={type}
										isGamePage={isGamePage}
									/>
								) : (
									<div
										style={{
											display: "flex",
											justifyContent: "flex-start",
											flexWrap: "wrap",
											gap: window.CASINO_GAME_CARD_V2 ? "5px 10px" : "10px",
										}}>
										{item.options.games.map((game) => (
											<GameCard
												key={game?.id}
												size={game.gameCardSize || "1x1"}
												game={data.gameList.find(
													(data) => game.id === data.id
												)}
												type={type}
												isGamePage={isGamePage}
											/>
										))}
									</div>
								)}
							</SwiperSlide>
						))}
					<div className="swiper-button-prev"></div>
					<div className="swiper-button-next"></div>
				</Swiper>
			</div>
		);
	};

	const renderSwiper = () => {
		const games = [...(data?.gameList || [])];
		return (
			<div
				data-testid={
					sectionStartIndex !== undefined
						? `scrollview-section${sectionStartIndex}-games`
						: undefined
				}>
				<Swiper
					spaceBetween={10}
					slidesPerView={"auto"}
					lazyPreloadPrevNext={1}
					slidesPerGroup={getSlidesPerGroup(windowWidth)}
					breakpoints={{ 1600: { slidesOffsetBefore: 50, slidesOffsetAfter: 50 } }}
					modules={[Pagination, Navigation]}
					navigation={{ nextEl: ".swiper-button-next", prevEl: ".swiper-button-prev" }}>
					{games
						.sort((g1, g2) =>
							data?.type === "userLastPlayed" ? -1 : g1?.order - g2?.order
						)
						.map((game, index) =>
							renderGameCard(
								game,
								index,
								data?.configs?.gameGrids[index]?.bannerSize || "1x1"
							)
						)}
					<div className="swiper-button-prev"></div>
					<div className="swiper-button-next"></div>
				</Swiper>
			</div>
		);
	};

	const topic = data?.topic?.[currentLanguage];

	if (!data?.isActive || !data?.gameList?.length || !topic) {
		return null;
	}

	const title = topic.includes("{user}") ? topic.replace("{user}", userData?.username) : topic;
	const firstSectionIdx = sectionStartIndex;
	const isSwiper =
		data?.configs?.gameGrids?.length > 0 ||
		topic.includes("{user}") ||
		["aiNewGamesForYou", "userLastPlayed", "aiBecauseYouLiked"].includes(data?.type);

	return (
		<CasinoSliderContainer
			innerRef={containerRef}
			isBackground={topic.includes("{user}")}>
			{data?.gameList?.length > 0 && (
				<TitleContainer>
					{data?.icon_url && <img src={data?.icon_url} />}
					<span
						data-testid={
							firstSectionIdx !== undefined
								? `text-casino-section-${firstSectionIdx}`
								: undefined
						}>
						{title}
					</span>
				</TitleContainer>
			)}

			{intersected
				? isSwiper
					? renderSwiper()
					: data?.configs?.gridOptions?.map(renderGridOptions)
				: null}
		</CasinoSliderContainer>
	);
};

export default CasinoSlider;
